import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ClientSidebar from '../../components/ClientSidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { Download, FileText, Search, ChevronDown, Menu } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTRACT_TYPE_LABELS = {
  'justificatif_revenus': 'Justificatif de revenus',
  'justificatif_domicile': 'Justificatif de domicile',
  'justificatif_identite': "Justificatif d'identité",
  'rib': 'RIB',
  'contrat_pret': 'Contrat de prêt',
  'contrat_rachat': 'Contrat de rachat de crédit',
  'echeancier': 'Échéancier',
  'tableau_amortissement': "Tableau d'amortissement",
  'avenant': 'Avenant',
  'attestation_assurance': "Attestation d'assurance",
  'releve_compte': 'Relevé de compte',
  'autre': 'Autre document'
};

const Documents = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadContracts();
    }
  }, [user?.id]);

  const loadContracts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin-contracts/client/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    } catch (error) {
      console.error('Error loading contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (contract) => {
    try {
      // Open the file in a new tab for download
      window.open(`${API_URL}/api/admin-contracts/file/${contract.fileName}`, '_blank');
    } catch (error) {
      console.error('Error downloading contract:', error);
    }
  };

  // Filter contracts
  const filteredContracts = (contracts || []).filter(contract => {
    if (!contract || !contract.originalName) return false;
    const matchesSearch = (contract.originalName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (CONTRACT_TYPE_LABELS[contract.contractType] || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || contract.contractType === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="flex min-h-screen">
      <ClientSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex-1 bg-gray-50">
        {/* Mobile Header - Black to match logo */}
        <div className="md:hidden bg-black text-white p-4 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
          <Link to="/client/dashboard">
            <img 
              src="https://customer-assets.emergentagent.com/job_younited-clone-1/artifacts/y6hpz6h2_IMG_0359.jpeg"
              alt="Younited"
              className="h-8 object-contain"
            />
          </Link>
          <div className="w-10"></div>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:block bg-gray-800 text-white py-3 px-8 text-sm">
          Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager.
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto p-4 md:p-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Documents & e-relevés</h1>
            <Button
              variant="outline"
              className="flex items-center text-xs md:text-sm"
              style={{ borderColor: '#8B5CF6', color: '#8B5CF6' }}
            >
              Gérer mes préférences
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Filters */}
          <Card className="p-4 md:p-6 mb-6">
            <div className="grid md:grid-cols-3 gap-3 md:gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue placeholder="Type de document" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="justificatif_revenus">Justificatif de revenus</SelectItem>
                  <SelectItem value="justificatif_domicile">Justificatif de domicile</SelectItem>
                  <SelectItem value="justificatif_identite">Justificatif d'identité</SelectItem>
                  <SelectItem value="rib">RIB</SelectItem>
                  <SelectItem value="contrat_pret">Contrat de prêt</SelectItem>
                  <SelectItem value="contrat_rachat">Contrat de rachat</SelectItem>
                  <SelectItem value="echeancier">Échéancier</SelectItem>
                  <SelectItem value="tableau_amortissement">Tableau d'amortissement</SelectItem>
                  <SelectItem value="avenant">Avenant</SelectItem>
                  <SelectItem value="attestation_assurance">Attestation d'assurance</SelectItem>
                  <SelectItem value="releve_compte">Relevé de compte</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-right text-xs md:text-sm text-gray-500 flex items-center justify-end">
                {filteredContracts.length} document(s) trouvé(s)
              </div>
            </div>
          </Card>

          {/* Documents - Mobile Cards */}
          <div className="md:hidden space-y-3">
            {loading ? (
              <Card className="p-6 text-center text-gray-500">Chargement...</Card>
            ) : filteredContracts.length === 0 ? (
              <Card className="p-6 text-center text-gray-500">Aucun document disponible</Card>
            ) : (
              filteredContracts.map((contract) => (
                <Card key={contract.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <FileText className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm truncate">{contract.originalName}</p>
                        <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium mt-1">
                          {CONTRACT_TYPE_LABELS[contract.contractType] || contract.contractType}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(contract.uploadedAt).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="rounded-full w-9 h-9 p-0 flex-shrink-0"
                      style={{ backgroundColor: '#8B5CF6' }}
                      onClick={() => handleDownload(contract)}
                    >
                      <Download className="h-4 w-4 text-white" />
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </div>

          {/* Documents Table - Desktop */}
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Document</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date d'ajout</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Télécharger</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : filteredContracts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      Aucun document disponible
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContracts.map((contract) => (
                    <TableRow key={contract.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <FileText className="h-5 w-5 text-red-500" />
                          <span className="font-medium text-gray-900">{contract.originalName}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                          {CONTRACT_TYPE_LABELS[contract.contractType] || contract.contractType}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {new Date(contract.uploadedAt).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {contract.description || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          className="rounded-full w-10 h-10 p-0"
                          style={{ backgroundColor: '#8B5CF6' }}
                          onClick={() => handleDownload(contract)}
                        >
                          <Download className="h-4 w-4 text-white" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>

          {/* Info Card */}
          <Card className="mt-6 p-4 md:p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <div className="flex items-start space-x-3 md:space-x-4">
              <div className="p-2 md:p-3 rounded-lg bg-white flex-shrink-0">
                <FileText className="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">
                  Vos contrats en ligne
                </h3>
                <p className="text-xs md:text-sm text-gray-600">
                  Retrouvez ici tous vos contrats, échéanciers et documents relatifs à votre crédit. 
                  Les documents sont ajoutés par votre conseiller et disponibles au téléchargement.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Documents;
